import type { LDClient } from 'launchdarkly-react-client-sdk';

import type { AuthContextValue } from '@/providers/auth/types';
import {
  buildLDContextFromAuth,
  identifyWithTimeout,
  isAnonymousContext,
} from '@/utils/launch-darkly-helpers';

import type { RouteGuardParams } from './types';

/**
 * Identifies authenticated users in LaunchDarkly if they have an anonymous context
 * @param ldClient - LaunchDarkly client instance
 * @param auth - Authentication context
 * @param params - Route parameters containing org and agent
 * @param routeLabel - Label for logging purposes
 */
export async function identifyUserInLaunchDarkly(
  ldClient: LDClient,
  auth: AuthContextValue,
  params: RouteGuardParams,
  routeLabel: string
): Promise<void> {
  if (!auth.isAuthenticated || auth.isLoading || !isAnonymousContext(ldClient)) {
    return;
  }

  console.log(
    `[${routeLabel}] User is authenticated but LaunchDarkly has anonymous context, attempting identification`
  );

  // Build LaunchDarkly context from auth data
  // We can get basic facility data from params, but full data would require API calls
  const facilityData = {
    organizationId: params.org,
    agentId: params.agent,
  };

  const ldContext = buildLDContextFromAuth(auth.user, facilityData);

  if (ldContext) {
    console.log(`[${routeLabel}] Identifying user in LaunchDarkly...`);
    const identified = await identifyWithTimeout(ldClient, ldContext, 2000);

    if (identified) {
      console.log(`[${routeLabel}] Successfully identified user in LaunchDarkly`);
    } else {
      console.warn(`[${routeLabel}] Failed to identify user in LaunchDarkly within timeout`);
    }
  }
}
